<template>
  <div class="trap-upsert">

    <div v-if="form.automatic" class="tu-auto-badge">
      <AeIcon name="Radio" :size="13" />
      {{ t('trapUpsert.autoBadge') }}
    </div>

    <!-- Riga campi principali: 4 col-6 -->
    <div class="tu-fields-grid">
      <div class="ae-form-row">
        <label>{{ t('trapUpsert.nameLabel') }}</label>
        <input v-model="form.name" type="text" class="form-control" :placeholder="t('trapUpsert.namePlaceholder')" />
      </div>
      <div class="ae-form-row">
        <label>{{ t('trapUpsert.typeLabel') }}</label>
        <select v-model="form.type" class="form-control">
          <option v-for="(label, val) in TRAP_TYPES" :key="val" :value="val">{{ label }}</option>
        </select>
      </div>
      <div class="ae-form-row pest-row">
        <label>{{ t('trapUpsert.pestLabel') }} <span class="label-hint">{{ t('trapUpsert.optional') }}</span></label>
        <div class="pest-input-wrap">
          <input
            v-model="form.pest"
            type="text"
            class="form-control"
            :placeholder="t('trapUpsert.pestPlaceholder')"
            @input="onPestInput"
            @blur="hideSuggestions"
            @focus="onPestInput"
            autocomplete="off"
          />
          <ul v-if="pestSuggestions.length > 0 && showPestSug" class="pest-sug-list">
            <li
              v-for="s in pestSuggestions"
              :key="s.name"
              class="pest-sug-item"
              @mousedown.prevent="pickPest(s)"
            >
              <span class="pest-sug-it">{{ s.name_it || s.name }}</span>
              <span v-if="s.name_it" class="pest-sug-sci">{{ s.name }}</span>
            </li>
          </ul>
        </div>
      </div>
      <div class="ae-form-row">
        <label>{{ t('trapUpsert.dateLabel') }}</label>
        <input v-model="form.date_trap" type="date" class="form-control" />
      </div>
    </div>

    <!-- Sezione appezzamenti + mappa -->
    <div class="tu-bottom">

      <!-- Sinistra: appezzamenti + coordinate -->
      <div class="tu-left">
        <div v-if="fields.length > 0" class="ae-form-row">
          <label>{{ t('trapUpsert.fieldsLabel') }}</label>
          <div class="plot-checklist">
            <label
              v-for="f in fields"
              :key="f.id_field"
              class="plot-check-item"
              :class="{ 'plot-check-item--selected': form.id_fields.includes(f.id_field) }"
            >
              <input
                type="checkbox"
                :value="f.id_field"
                :checked="form.id_fields.includes(f.id_field)"
                @change="toggleField(f.id_field)"
              />
              <span>{{ fieldLabel(f) }}</span>
            </label>
          </div>
        </div>

        <div class="ae-form-row">
          <label>{{ t('trapUpsert.gpsLabel') }} <span class="label-hint">{{ t('trapUpsert.gpsHint') }}</span></label>
          <div class="coords-row">
            <div class="coord-field">
              <span class="coord-label">{{ t('trapUpsert.lat') }}</span>
              <input v-model.number="form.lat" type="number" step="0.000001" class="form-control"  />
            </div>
            <div class="coord-field">
              <span class="coord-label">{{ t('trapUpsert.lon') }}</span>
              <input v-model.number="form.lon" type="number" step="0.000001" class="form-control"  />
            </div>
            <button v-if="form.lat && form.lon" type="button" class="coord-clear" @click="form.lat = null; form.lon = null" :title="t('trapUpsert.removeCoords')">×</button>
          </div>
        </div>

      </div>

      <!-- Destra: mappa (nascosta su mobile) -->
      <div class="tu-map">
        <div class="map-hint">
          <AeIcon name="MapPin" :size="14" />
          {{ form.lat && form.lon ? `${form.lat.toFixed(5)}, ${form.lon.toFixed(5)}` : t('trapUpsert.mapHintDefault') }}
        </div>
        <AeMap :opt="mapOpt" :layers="mapLayers" @click="onMapClick" />
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { translateCropName } from '../i18n/cropVocab'
import { useDbStore } from '../stores/dbStore.js'
import { AeButton, AeMap, AeIcon } from '@aevue/components'
import { api } from '../services/api.js'
import { baseLayers } from '../composables/useMapLayers.js'

const { t } = useI18n()

const props = defineProps({
  trapId:             { type: String, default: null },
  idFarmCenter:       { type: String, required: true },
  idSeason:           { type: String, default: null },
  preSelectedPlotIds: { type: Array, default: () => [] }
})

const emit = defineEmits(['submit', 'cancel'])

const dbStore = useDbStore()
const saving      = ref(false)
const plots       = ref([])
const fields      = ref([])
const crops       = ref([])
const allInsects  = ref([])
const showPestSug = ref(false)
const farmCenter  = ref(null)

const TRAP_TYPES = computed(() => ({
  cromo:   t('trapUpsert.typeCromo'),
  pheno:   t('trapUpsert.typePheno'),
  flytrap: t('trapUpsert.typeFlytrap'),
  other:   t('trapUpsert.typeOther')
}))

const form = ref({
  name:      '',
  type:      'pheno',
  pest:      '',
  date_trap: new Date().toISOString().split('T')[0],
  id_fields: [],
  lat:       null,
  lon:       null,
})

// ── Autocomplete parassiti ────────────────────────────────────────────────────

const pestSuggestions = computed(() => {
  const q = (form.value.pest || '').trim().toLowerCase()
  if (!q || q.length < 2) return []
  return allInsects.value.filter(s =>
    (s.name_it || '').toLowerCase().includes(q) ||
    (s.name || '').toLowerCase().includes(q)
  ).slice(0, 10)
})

function onPestInput() { showPestSug.value = true }
function hideSuggestions() { setTimeout(() => { showPestSug.value = false }, 150) }
function pickPest(s) { form.value.pest = s.name_it || s.name; showPestSug.value = false }

// ── Mappa ─────────────────────────────────────────────────────────────────────

// Estrae la geometria GeoJSON da un plot (geojson ha precedenza su polygon_coordinates)
function plotGeometry(plot) {
  if (plot.geojson) {
    const g = typeof plot.geojson === 'string' ? JSON.parse(plot.geojson) : plot.geojson
    return g.type === 'FeatureCollection' ? g.features[0]?.geometry : g
  }
  if (plot.polygon_coordinates?.length > 0) {
    return { type: 'Polygon', coordinates: [plot.polygon_coordinates.map(([lat, lon]) => [lon, lat])] }
  }
  return null
}

// Layer base: tutti i plot come poligoni grigi, SENZA popup né click handler.
// useMapLayers non viene usato qui perché aggiunge link "Vai al campo" che intercettano
// il click sulla mappa impedendo il posizionamento del marker della trappola.
const basePlotLayer = computed(() => {
  const features = plots.value
    .map(p => ({ geometry: plotGeometry(p), name: p.name }))
    .filter(p => p.geometry)
    .map(p => ({ type: 'Feature', geometry: p.geometry, properties: { name: p.name } }))
  if (!features.length) return null
  return {
    name: 'Appezzamenti',
    type: 'geojson',
    data: { type: 'FeatureCollection', features },
    options: {
      style: { fillColor: '#94a3b8', color: '#64748b', weight: 1.5, fillOpacity: 0.25 }
      // nessun popup, nessun click handler → i click raggiungono sempre onMapClick
    }
  }
})

const mapCenter = computed(() => {
  if (form.value.lat && form.value.lon) return [form.value.lat, form.value.lon]
  const fc = farmCenter.value
  if (fc?.lat && fc?.lon) return [fc.lat, fc.lon]
  return [42.5, 12.5]
})

const mapOpt = computed(() => ({
  zoom: 14,
  height: window.innerWidth <= 640 ? '220px' : '380px',
  baseLayers,
  layer_selector: false,
  center: { lat: mapCenter.value[0], lon: mapCenter.value[1] }
}))

// field non ha geometria propria: evidenzia il plot che contiene i field selezionati
const selectedPlotsLayer = computed(() => {
  if (!form.value.id_fields.length) return null
  const selectedPlotIds = new Set(
    form.value.id_fields
      .map(id => fields.value.find(f => f.id_field === id)?.id_plot)
      .filter(Boolean)
  )
  const features = [...selectedPlotIds]
    .map(id => plots.value.find(p => p.id_plot === id))
    .filter(Boolean)
    .map(plot => ({ geometry: plotGeometry(plot), name: plot.name }))
    .filter(p => p.geometry)
    .map(p => ({ type: 'Feature', geometry: p.geometry, properties: { name: p.name } }))
  if (!features.length) return null
  return {
    name: 'Plot selezionati',
    type: 'geojson',
    no_fit_bounds: true,
    data: { type: 'FeatureCollection', features },
    options: {
      style: { fillColor: '#f6e05e', color: '#d69e2e', weight: 3, fillOpacity: 0.45 }
      // nessun popup: il click deve sempre raggiungere onMapClick
    }
  }
})

const mapLayers = computed(() => {
  const layers = []
  if (basePlotLayer.value) layers.push(basePlotLayer.value)
  if (selectedPlotsLayer.value) layers.push(selectedPlotsLayer.value)
  if (form.value.lat && form.value.lon) {
    layers.push({
      name: 'Trappola',
      no_fit_bounds: true,
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [form.value.lon, form.value.lat] },
          properties: { name: form.value.name || t('trapUpsert.trapNameFallback') }
        }]
      },
      options: {
        point_to_circle: true,
        circleOptions: { radius: 9, fillOpacity: 0.9, color: '#fff', weight: 2, fillColor: '#e53e3e' },
        popup: true,
        popupTemplate: (p) => `<strong>📍 ${p.name}</strong>`
      }
    })
  }
  return layers
})

function onMapClick(e) {
  if (!e?.latlng) return
  form.value.lat = Math.round(e.latlng.lat * 1000000) / 1000000
  form.value.lon = Math.round(e.latlng.lng * 1000000) / 1000000
}

function onCoordsManual() { /* il layer si aggiorna via computed */ }

// ── Fields ────────────────────────────────────────────────────────────────────

function toggleField(id_field) {
  const idx = form.value.id_fields.indexOf(id_field)
  if (idx === -1) form.value.id_fields.push(id_field)
  else form.value.id_fields.splice(idx, 1)
}

// Etichetta del field: usa il nome se presente, altrimenti "Plot - Coltura"
function fieldLabel(f) {
  if (f.name) return f.name
  const plot = plots.value.find(p => p.id_plot === f.id_plot)
  const crop = crops.value.find(c => c.id_crop === f.id_crop)
  const parts = [plot?.name, translateCropName(crop?.name || crop?.crop_name)].filter(Boolean)
  return parts.length ? parts.join(' - ') : f.id_field
}

// ── Init ──────────────────────────────────────────────────────────────────────

onMounted(async () => {
  // insects carica in background — non blocca il form
  api.get('/api/v2/poderi2/insects').then(insRes => {
    if (insRes?.ok) allInsects.value = insRes.data || []
  })

  const [plotsRes, fieldsRes, cropsRes, fcRes] = await Promise.all([
    dbStore.select('plot', { id_farm_center: props.idFarmCenter }),
    dbStore.select('field', { id_farm_center: props.idFarmCenter, id_season: props.idSeason }),
    dbStore.select('crop'),
    dbStore.selectOne('farm_center', props.idFarmCenter)
  ])
  plots.value  = plotsRes?.data  || []
  fields.value = fieldsRes?.data || []
  crops.value  = cropsRes?.data  || []
  if (fcRes?.ok) farmCenter.value = fcRes.data

  if (!props.trapId) {
    if (props.preSelectedPlotIds?.length) {
      const plotSet = new Set(props.preSelectedPlotIds)
      form.value.id_fields = fields.value
        .filter(f => plotSet.has(f.id_plot))
        .map(f => f.id_field)
    }
    return
  }
  const result = await dbStore.selectOne('trap', props.trapId)
  if (result?.ok && result?.data) {
    const t = result.data
    form.value = {
      name:      t.name      || '',
      type:      t.type      || 'pheno',
      pest:      t.pest      || '',
      date_trap: t.date_trap || new Date().toISOString().split('T')[0],
      id_fields: Array.isArray(t.id_fields) ? [...t.id_fields] : [],
      lat:       t.lat  ?? null,
      lon:       t.lon  ?? null,
    }
  }
})

// ── Save ──────────────────────────────────────────────────────────────────────

defineExpose({ save, saving })

async function save() {
  if (!form.value.name.trim()) {
    alert(t('trapUpsert.nameRequired'))
    return
  }
  saving.value = true
  try {
    const data = { ...form.value, id_farm_center: props.idFarmCenter, id_season: props.idSeason }
    if (props.trapId) {
      await dbStore.update('trap', { id_trap: props.trapId }, data)
      emit('submit', props.trapId)
    } else {
      const result = await dbStore.insert('trap', data)
      if (result.ok) emit('submit', result.data)
    }
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.trap-upsert {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Griglia 4 campi principali: 2 per riga */
.tu-fields-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

/* Sezione inferiore: appezzamenti a sinistra, mappa a destra */
.tu-bottom {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: start;
}

.tu-left {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.tu-map {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.map-hint {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
  color: var(--text-muted, #718096);
  min-height: 1.2rem;
}

@media (max-width: 640px) {
  .tu-fields-grid {
    grid-template-columns: 1fr;
  }
  .tu-bottom {
    grid-template-columns: 1fr;
  }
}

.ae-form-row {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.ae-form-row label {
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-primary, #2d3748);
}

.label-hint {
  font-weight: 400;
  font-size: 0.8rem;
  color: var(--text-muted, #718096);
}

.form-control {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--border-color, #ddd);
  border-radius: var(--border-radius, 8px);
  font-size: 1rem;
  font-family: inherit;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: var(--primary-color, #4299e1);
  box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
}

/* Coordinate */
.coords-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.coord-field {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.coord-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted, #718096);
  white-space: nowrap;
  flex-shrink: 0;
}

.coord-field .form-control {
  padding: 0.45rem 0.5rem;
  font-size: 0.9rem;
}

.coord-clear {
  background: none;
  border: 1px solid var(--border-color, #ddd);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--text-muted, #718096);
}
.coord-clear:hover { background: #fee2e2; color: #e53e3e; border-color: #e53e3e; }

/* Appezzamenti */
.plot-checklist {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.5rem 0.6rem;
  border: 1px solid var(--border-color, #ddd);
  border-radius: var(--border-radius, 8px);
  max-height: 150px;
  overflow-y: auto;
}

.plot-check-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.25rem 0.4rem;
  border-radius: 4px;
  transition: background 0.15s;
}

.plot-check-item--selected {
  background: #fefcbf;
  font-weight: 600;
}

/* Pest autocomplete */
.pest-input-wrap {
  position: relative;
}

.pest-sug-list {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 200;
  list-style: none;
  margin: 0;
  padding: 0;
  background: #fff;
  border: 1px solid var(--border-color, #ddd);
  border-radius: var(--border-radius, 8px);
  box-shadow: 0 4px 16px rgba(0,0,0,0.18);
  max-height: 200px;
  overflow-y: auto;
}

.pest-sug-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.4rem 0.7rem;
  cursor: pointer;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  gap: 0.5rem;
}
.pest-sug-item:last-child { border-bottom: none; }
.pest-sug-item:hover { background: rgba(66,153,225,0.08); }

.pest-sug-it  { font-size: 0.88rem; font-weight: 500; }
.pest-sug-sci { font-size: 0.75rem; color: var(--text-muted, #718096); font-style: italic; }

.tu-auto-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  border-radius: 6px;
  background: rgba(66, 153, 225, 0.1);
  color: #2b6cb0;
  font-size: 0.82rem;
  font-weight: 500;
  margin-bottom: 12px;
}

</style>
